/* Print only chosen CN22 labels from a generated label file — same size/layout, nothing saved. */
import { useEffect, useState } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Printer, ListChecks } from "lucide-react";

const printBlob = (data) => {
  const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
  const fr = document.createElement("iframe");
  fr.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
  fr.src = url;
  fr.onload = () => { try { fr.contentWindow.focus(); fr.contentWindow.print(); } catch { window.open(url, "_blank"); } };
  document.body.appendChild(fr);
  setTimeout(() => { fr.remove(); URL.revokeObjectURL(url); }, 120000);
};

export const SelectLabelsButton = ({ onClick, busy, testId, className = "" }) => (
  <Button variant="ghost" size="sm" onClick={onClick} disabled={busy} title="Print selected parcels only"
    className={`h-7 w-7 p-0 text-violet-600 ${className}`} data-testid={testId}>
    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ListChecks className="w-4 h-4" />}
  </Button>
);

export const SelectedLabelsDialog = ({ entryId, onClose }) => {
  const [d, setD] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [wts, setWts] = useState({});
  useEffect(() => {
    setD(null); setSel(new Set()); setWts({});
    if (!entryId) return;
    axios.get(`${API}/bulk-label/history/${entryId}/labels`, { headers: getAuthHeader() })
      .then(r => setD(r.data)).catch(e => { toast.error(e.response?.data?.detail || "Could not read the labels"); onClose(); });
  }, [entryId]);
  const toggle = (i) => setSel(s => { const n = new Set(s); n.has(i) ? n.delete(i) : n.add(i); return n; });
  const all = d?.labels || [];
  const print = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/bulk-label/history/${entryId}/print-selected`, { indexes: [...sel], weights: Object.fromEntries(Object.entries(wts).filter(([i, v]) => sel.has(Number(i)) && Number(v) > 0)) },
        { headers: getAuthHeader(), responseType: "blob", timeout: 60000 });
      printBlob(r.data);
      const typed = Object.keys(wts).filter(i => sel.has(Number(i)) && Number(wts[i]) > 0).length;
      toast.success(`Printing ${sel.size} label(s)${typed ? ` · ${typed} weight(s) saved & synced to all documents` : ""}`); onClose();
    } catch { toast.error("Could not make the selected labels"); }
    setBusy(false);
  };
  const lpp = d?.labels_per_page || 4;
  return (
    <Dialog open={!!entryId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="bl-select-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ListChecks className="w-5 h-5 text-violet-600" /> Print selected CN22 labels</DialogTitle>
          <DialogDescription>Tick the parcels to print. Same label size and layout ({d ? `${d.layout} · ${lpp} per page` : "…"}). Type a weight (grams) for parcels without one — it prints on the label and syncs to every document.</DialogDescription>
        </DialogHeader>
        {!d ? <div className="py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></div> : (<>
          <div className="flex items-center gap-2 text-xs">
            <button type="button" className="rounded border px-2 py-1 hover:bg-slate-50" onClick={() => setSel(new Set(all.map(l => l.index)))} data-testid="bl-select-all">Select all</button>
            <button type="button" className="rounded border px-2 py-1 hover:bg-slate-50" onClick={() => setSel(new Set())} data-testid="bl-select-none">Clear</button>
            <span className="ml-auto text-slate-500" data-testid="bl-select-count">{sel.size} of {all.length} · {Math.ceil(sel.size / lpp) || 0} page(s)</span>
          </div>
          <div className="max-h-80 overflow-y-auto rounded border divide-y" data-testid="bl-select-list">
            {all.map((l, n) => (
              <label key={l.index} className={`flex cursor-pointer items-center gap-2 px-2 py-1.5 text-xs ${sel.has(l.index) ? "bg-violet-50" : "hover:bg-slate-50"}`} data-testid={`bl-select-row-${l.index}`}>
                <input type="checkbox" checked={sel.has(l.index)} onChange={() => toggle(l.index)} data-testid={`bl-select-check-${l.index}`} />
                <span className="w-6 text-slate-400">{n + 1}</span>
                <span className="flex-1 truncate font-medium text-slate-800">{l.customer_name || "—"}</span>
                <span className="font-mono text-slate-600">{l.tracking || "no tracking"}</span>
                {l.parcel && <span className="rounded bg-emerald-100 px-1.5 font-semibold text-emerald-800">{l.parcel}</span>}
                {l.weight
                  ? <span className="w-16 text-right font-semibold text-slate-700" data-testid={`bl-select-weight-${l.index}`}>{l.weight} g</span>
                  : <input type="number" min="1" inputMode="decimal" placeholder="g" value={wts[l.index] || ""}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => { const v = e.target.value; setWts(w => ({ ...w, [l.index]: v })); if (Number(v) > 0) setSel(s => new Set(s).add(l.index)); }}
                      className="h-6 w-16 rounded border border-amber-300 bg-amber-50 px-1 text-right" data-testid={`bl-select-weight-input-${l.index}`} />}
              </label>))}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" className="bg-violet-600 hover:bg-violet-700" disabled={!sel.size || busy} onClick={print} data-testid="bl-select-print">
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Printer className="w-3.5 h-3.5 mr-1" />} Print {sel.size || ""} selected
            </Button>
          </div>
        </>)}
      </DialogContent>
    </Dialog>
  );
};
